import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useToast } from "../components/ui/Toast";
import { useImmer } from "use-immer";
import {
  addDays,
  formatIsoString,
  parseDate,
  todayForPlan,
} from "../utils/dateUtils";
import { PersonLookup } from "../data/lookups";
import { compareMealInds, mealInds } from "../utils/mealIndUtils";
import { Intake } from "../data/intake";
import { flatMap, groupBy, keys, values } from "lodash";
import {
  JustIntake,
  MealWithIntakes,
  PlanCell,
  PlanCellCoordinates,
  PlanCellCoordinatesWithOptionalPerson,
  PlanCellCoordinatesWithoutPerson,
  PlanCellWithIndex,
} from "../data/plan";
import {
  computePlanCellCoordinates,
  computePlanCellCoordinatesFromPlanCellWithIndex,
  CookingOverrides,
  createCustomIntakes,
  findById,
  getMealKeyForMeal,
  isNewCell,
  isNewOrEdited,
  isNullIndex,
  mapPlanCellToNutriValues,
  MealKey,
} from "../utils/planUtils";
import { MealWithIngredients } from "../data/mealWithIngredients";
import {
  DrawerHistoryContext,
  MealPlannerContext,
  MealPlannerContextType,
} from "../contexts";
import { CookingDrawer, useCookingDrawer } from "../components/CookingDrawer";
import { isDeleted, isEdited } from "../utils/editUtils";
import { getNeedForDate } from "../utils/personUtils";
import { eachDayOfInterval } from "date-fns";
import { wrapSave } from "../components/buttons/SaveButton";
import {
  getIntakes,
  getMeals,
  PurchaseDto,
  savePlan as savePlanRequest,
  updateNeeds,
} from "../api";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys, useDepartments, useShops } from "../queries/queries";
import {
  ensureFoods,
  ensurePurchases,
  getConsumeLookups,
  getFoodByUnit,
  getPurchaseById,
  getPurchasesByFood,
  useFoodsByIdsAsync,
  useJustIntakePurchasesAsync,
  useMealPurchasesAsync,
} from "../queries/caches";
import { collectFoodIds, collectPurchaseIds } from "../utils/foodIdUtils";
import { LoadingComponent } from "../components/LoadingComponent";
import { useUnloadAlert } from "../components/useUnloadAlert";
import { AddPlanCellDrawer } from "../components/AddPlanCellDrawer";
import { deepEquals } from "../utils/equalUtils";
import { PlanCellTag } from "../components/plan/PlanCellTag";
import { PlanToolbar } from "./mealPlanner/PlanToolbar";
import { PersonNeedDrawer } from "./mealPlanner/PersonNeedDrawer";
import {
  getPlanCell,
  getPlanCells,
  getPlanCellsForDateAndMeal,
  getPlanCellsWithIndex,
  setPlanCell,
  TableData,
} from "../utils/planTableData";
import { EditPlanCellDrawer } from "../components/EditPlanCellDrawer";
import { Plan2PlateIconButton } from "../components/buttons/Plan2PlateIconButton";
import { AllNutriValues } from "../components/AllNutriValues";
import { usePlan2PlateDisclosure } from "../components/Plan2PlateDrawer";
import { NeedDefinitionWithEditedFlags } from "../data/need";
import { needToNeedDefinition } from "../utils/needMath";
import { useDrawerHistory } from "../components/drawerHistory";
import { useHorizontalSwipe } from "../utils/useHorizontalSwipe";
import { SettingsDrawer } from "../components/SettingsDrawer";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { useCurrentUser } from "../CurrentUserContext";
import { PlanAllDrawer, usePlanAllDrawer } from "../components/PlanAllDrawer";
import { useShowHints, useTrackingMode } from "../data/settings";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/Tooltip";

interface Props {}

const MealTable: React.FC<Props> = () => {
  const queryClient = useQueryClient();
  const {
    activeGroupId,
    members,
    membersGroupId,
    personId,
    setActiveGroupId,
  } = useCurrentUser();
  const [startDate, setStartDate] = useState(todayForPlan());
  const [endDate, setEndDate] = useState(addDays(todayForPlan(), 0));

  const relevantDates = useMemo(
    () =>
      endDate.getTime() < startDate.getTime()
        ? []
        : eachDayOfInterval({
            start: startDate,
            end: endDate,
          }).map(formatIsoString),
    [startDate, endDate],
  );
  const [personLookup, updatePersonLookup] = useImmer<PersonLookup>({});

  const [tableData, updateTableData] = useImmer<TableData>({});
  const [hasNewData, updateHasNewData] = useImmer<boolean>(false);
  const [shouldReload, updateShouldReload] = useImmer<boolean>(true);
  const [resetPlan, updateResetPlan] = useImmer<boolean>(true);
  const [singleDayMode, updateSingleDayMode] = useImmer<boolean>(true);

  // Gruppenwechsel: erst nach Bestätigung (bei ungespeicherten Änderungen).
  const [pendingGroupId, setPendingGroupId] = useState<number | null>(null);
  const lastGroupIdRef = useRef<number | null>(null);

  const { pristine, setPristine } = useUnloadAlert();

  useEffect(() => {
    setPristine(!hasNewData);
  }, [hasNewData]);

  // Beim Wechsel der aktiven Gruppe den Plan verwerfen und neu laden, da sich
  // die Personen der Sicht ändern.
  useEffect(() => {
    if (
      lastGroupIdRef.current !== null &&
      lastGroupIdRef.current !== activeGroupId
    ) {
      updatePersonLookup({});
      updateTableData({});
      updateResetPlan(true);
      updateShouldReload(true);
    }
    lastGroupIdRef.current = activeGroupId;
  }, [
    activeGroupId,
    updatePersonLookup,
    updateTableData,
    updateResetPlan,
    updateShouldReload,
  ]);

  const requestGroupChange = useCallback(
    (groupId: number) => {
      if (groupId === activeGroupId) {
        return;
      }
      if (hasNewData) {
        setPendingGroupId(groupId);
      } else {
        setActiveGroupId(groupId);
      }
    },
    [activeGroupId, hasNewData, setActiveGroupId],
  );

  const toast = useToast();

  const shops = useShops();
  const departments = useDepartments();

  const [isLoadingUp, setIsLoadingUp] = useState(false);
  const [isLoadingDown, setIsLoadingDown] = useState(false);

  const getPlanCellsFromTableData = (tableData: TableData) =>
    flatMap(
      flatMap(
        flatMap(values(tableData), (x) => values(x)),
        (x) => values(x),
      ),
      (x) => values(x),
    );

  const planCellsFromTableData = getPlanCellsFromTableData(tableData);

  const planCellMeals = useMemo(
    () =>
      planCellsFromTableData
        .filter(
          (planCell): planCell is MealWithIntakes =>
            planCell.indicator === "MealWithIntakes",
        )
        .map((planCell) => planCell.meal),
    [tableData],
  );
  const mealPurchases = useMealPurchasesAsync(planCellMeals);
  const justIntakePurchases = useJustIntakePurchasesAsync(
    planCellsFromTableData.filter(
      (planCell): planCell is JustIntake => planCell.indicator === "JustIntake",
    ),
  );
  const purchases = [...mealPurchases, ...justIntakePurchases];
  const foodIds = useMemo(
    () => collectFoodIds(planCellMeals, purchases),
    [planCellMeals, purchases],
  );
  const foods = useFoodsByIdsAsync(foodIds);

  const loadTableData = useCallback(async () => {
    if (!shouldReload) {
      return;
    }
    // Erst laden, wenn die Mitglieder der aktiven Gruppe bekannt sind – sonst
    // fehlen die Person-Spalten und das Befüllen der Tabelle schlägt fehl.
    if (members.length === 0 || membersGroupId !== activeGroupId) {
      return;
    }
    try {
      let mealData: MealWithIngredients[];
      let intakeData: Intake[];
      if (!relevantDates.length) {
        mealData = [];
        intakeData = [];
      } else {
        const startDate = relevantDates[0];
        const endDate = relevantDates[relevantDates.length - 1];
        updateShouldReload(false);

        mealData = (
          await getMeals({
            query: { startDate, endDate, groupId: activeGroupId ?? undefined },
          })
        ).data;

        intakeData = (
          await getIntakes({
            query: { startDate, endDate, groupId: activeGroupId ?? undefined },
          })
        ).data;
      }

      // Aktuell angemeldete Person zuerst, danach die übrigen lexikografisch.
      const persons = [...members].sort((a, b) => {
        if (a.id === personId) {
          return -1;
        }
        if (b.id === personId) {
          return 1;
        }
        return (a.name ?? "").localeCompare(b.name ?? "", "de");
      });
      queryClient.setQueryData(
        queryKeys.persons(false, activeGroupId),
        persons,
      );

      const intakeByMeal = groupBy(intakeData, "mealId");

      const personLookupNew: typeof personLookup = {};

      persons.forEach((person) => {
        personLookupNew[person.id] = person;
      });

      const newData: TableData = {};
      for (const relevantDate of relevantDates) {
        newData[relevantDate] = {};
        for (const mealInd of keys(mealInds)) {
          newData[relevantDate][mealInd] = {};
          for (const personIdAsString of keys(personLookupNew)) {
            const personId = Number(personIdAsString);
            newData[relevantDate][mealInd][personId] = [];
          }
        }
      }

      mealData.forEach((meal) => {
        const intakesForMeal = intakeByMeal[meal.id];
        const { mealDate, mealInd, personId } = meal;
        if (!newData[mealDate]?.[mealInd]?.[personId]) {
          return;
        }
        const mealWithIntake: MealWithIntakes = {
          indicator: "MealWithIntakes",
          meal: meal,
          intakes: intakesForMeal,
        };
        newData[mealDate][mealInd][personId].push(mealWithIntake);
      });
      intakeData
        .filter((intake) => intake.mealId == null)
        .forEach((intake) => {
          const { intakeDate, mealInd, personId } = intake;
          if (!newData[intakeDate]?.[mealInd]?.[personId]) {
            return;
          }
          const unplannedIntake: JustIntake = {
            indicator: "JustIntake",
            ...intake,
          };
          newData[intakeDate][mealInd][personId].push(unplannedIntake);
        });
      if (!resetPlan) {
        const existingPlanCells = getPlanCellsFromTableData(tableData);
        existingPlanCells
          .filter((c) => isNewOrEdited(c))
          .forEach((pC) => {
            const { date, mealInd, personId } = computePlanCellCoordinates(pC);
            newData[date][mealInd][personId].push(pC);
          });
      }

      // Foods/Purchases vorab in den (non-suspending) Batch-Cache laden, damit
      // der erste Render nach dem Laden vollständige Nährwerte zeigt.
      const planCellsFromNewTableData = getPlanCellsFromTableData(newData);
      const newPlanCellMeals = planCellsFromNewTableData
        .filter(
          (planCell): planCell is MealWithIntakes =>
            planCell.indicator === "MealWithIntakes",
        )
        .map((planCell) => planCell.meal);
      const justIntakePurchaseIds = planCellsFromNewTableData
        .filter(
          (planCell): planCell is JustIntake =>
            planCell.indicator === "JustIntake",
        )
        .map((planCell) => planCell.purchaseUnitPurchaseId);
      const purchaseIds = collectPurchaseIds(newPlanCellMeals);
      const baseFoodIds = collectFoodIds(newPlanCellMeals);
      await ensurePurchases(
        queryClient,
        [...purchaseIds, ...justIntakePurchaseIds],
        baseFoodIds,
      );
      const cachedPurchases = [
        ...justIntakePurchaseIds.map((id) => getPurchaseById(queryClient, id)),
        ...purchaseIds.map((id) => getPurchaseById(queryClient, id)),
        ...baseFoodIds.flatMap((id) => getPurchasesByFood(queryClient, id)),
      ].filter((purchase): purchase is PurchaseDto => purchase != null);
      const purchaseFoodIds = [
        ...new Set(
          cachedPurchases
            .map((purchase) => purchase.foodId)
            .filter((id): id is number => id != null && id > 0),
        ),
      ];
      await ensurePurchases(queryClient, [], purchaseFoodIds);
      await ensureFoods(queryClient, [
        ...new Set([
          ...collectFoodIds(newPlanCellMeals, cachedPurchases),
          ...purchaseFoodIds,
        ]),
      ]);

      updatePersonLookup(personLookupNew);
      updateTableData(newData);
      updateShouldReload(false);
      updateResetPlan(false);
      setIsLoadingUp(false);
      setIsLoadingDown(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  }, [
    queryClient,
    updatePersonLookup,
    updateTableData,
    updateShouldReload,
    setIsLoadingUp,
    setIsLoadingDown,
    resetPlan,
    getPlanCellsFromTableData,
    tableData,
    shouldReload,
    relevantDates,
    members,
    membersGroupId,
    activeGroupId,
  ]);

  function loadPreviousDay() {
    updateShouldReload(true);
    setStartDate(addDays(startDate, -1));
    setEndDate(addDays(endDate, -1));
  }

  function loadNextDay() {
    updateShouldReload(true);
    setStartDate(addDays(startDate, 1));
    setEndDate(addDays(endDate, 1));
  }

  const onConsume = async (
    date: string,
    mealInd: string,
    mealKey: MealKey | undefined,
    personIdParam: number | undefined,
    overrides?: CookingOverrides,
  ) => {
    const planCellsWithIndex: PlanCellWithIndex[] = flatMap(
      keys(personLookup),
      (personId) => {
        const planCellsForCoords = getPlanCells(tableData, {
          date,
          mealInd,
          personId: Number(personId),
        });
        return planCellsForCoords
          .map((planCell, index) => {
            return {
              planCell,
              index,
            };
          })
          .filter((pC) => {
            const coordinates =
              computePlanCellCoordinatesFromPlanCellWithIndex(pC);
            if (pC.planCell.indicator != "MealWithIntakes") {
              return (
                personIdParam == undefined ||
                coordinates.personId == personIdParam
              );
            }
            const meal = pC.planCell.meal;
            return (
              (mealKey == undefined ||
                deepEquals(getMealKeyForMeal(meal), mealKey)) &&
              (personIdParam == undefined || meal.personId == personIdParam)
            );
          });
      },
    );

    let preparedPlanCells = planCellsWithIndex;
    if (overrides) {
      // Foods/Purchases verbindlich laden, bevor die Intakes berechnet werden.
      // Sonst kann `createCustomIntakes` mit noch nicht geladenen (asynchronen)
      // Lookup-Arrays leere Intakes erzeugen.
      const mealsToConsume = planCellsWithIndex
        .filter((pcI) => pcI.planCell.indicator === "MealWithIntakes")
        .map((pcI) => (pcI.planCell as MealWithIntakes).meal);
      const consumeFoodIds = collectFoodIds(mealsToConsume);
      const consumePurchaseIds = collectPurchaseIds(mealsToConsume);
      // Gewählte Food-Alternativen (Override mit FoodUnit-Id, ohne Purchase)
      // müssen ebenfalls aufgelöst werden, sonst fällt die Erzeugung des
      // Verzehrs auf das ursprüngliche Food der MealIngredient zurück.
      const overrideFoodUnitIds = Object.values(overrides)
        .filter((o) => o.purchaseId == null && o.unitId != null && o.unitId > 0)
        .map((o) => o.unitId!);
      // Explizit gewählte Produkte (auch von alternativen Foods) müssen geladen
      // sein, sonst fällt der Verzehr auf das ursprüngliche Food zurück.
      const overridePurchaseIds = Object.values(overrides)
        .map((o) => o.purchaseId)
        .filter((id): id is number => id != null && id > 0);
      try {
        await ensureFoods(queryClient, consumeFoodIds, overrideFoodUnitIds);
        const alternativeFoodIds = overrideFoodUnitIds
          .map((unitId) => getFoodByUnit(queryClient, unitId)?.id)
          .filter((id): id is number => id != null);
        await ensurePurchases(
          queryClient,
          [...consumePurchaseIds, ...overridePurchaseIds],
          [...consumeFoodIds, ...alternativeFoodIds],
        );
      } catch (error) {
        // Fehlende Lookups dürfen den Verzehr nicht verhindern – es wird mit
        // dem verwendet, was im Cache vorhanden ist.
        console.error("Lookups für Verzehr unvollständig", error);
      }
      const cached = getConsumeLookups(
        queryClient,
        mealsToConsume,
        overrideFoodUnitIds,
      );
      const overridePurchases = overridePurchaseIds
        .map((id) => getPurchaseById(queryClient, id))
        .filter((purchase): purchase is PurchaseDto => purchase != null);
      const effectivePurchases = [
        ...new Map(
          [...purchases, ...cached.purchases, ...overridePurchases].map((p) => [
            p.id,
            p,
          ]),
        ).values(),
      ];
      const effectiveFoods = [
        ...new Map([...foods, ...cached.foods].map((f) => [f.id, f])).values(),
      ];

      preparedPlanCells = planCellsWithIndex.map((pcI) => {
        if (pcI.planCell.indicator === "MealWithIntakes") {
          const mealWithIntakes = pcI.planCell as MealWithIntakes;
          // Apply custom intakes
          const result = createCustomIntakes(
            mealWithIntakes,
            effectivePurchases,
            effectiveFoods,
            overrides,
          );
          return {
            ...pcI,
            planCell: {
              ...mealWithIntakes,
              intakes: result.intakes,
              meal: result.updatedMeal || mealWithIntakes.meal,
              edited: result.updatedMeal ? true : mealWithIntakes.edited,
            },
          };
        }
        return pcI;
      });
    }
    submitPlanCells(preparedPlanCells);
  };
  const drawerHistory = useDrawerHistory();

  const onDeleteMeal = (
    date: string,
    mealInd: string,
    mealKey: MealKey,
    personIdParam?: number,
  ) => {
    const planCellsWithIndex: PlanCellWithIndex[] = flatMap(
      keys(personLookup),
      (personId) => {
        const pId = Number(personId);
        if (personIdParam !== undefined && pId !== personIdParam) {
          return [];
        }
        const planCellsForCoords = getPlanCells(tableData, {
          date,
          mealInd,
          personId: pId,
        });
        return planCellsForCoords
          .map((planCell, index) => ({ planCell, index }))
          .filter(
            (pC) =>
              pC.planCell.indicator === "MealWithIntakes" &&
              deepEquals(
                getMealKeyForMeal((pC.planCell as MealWithIntakes).meal),
                mealKey,
              ),
          );
      },
    );
    for (const pcI of planCellsWithIndex) {
      submitPlanCells([
        { planCell: { ...pcI.planCell, deleted: true }, index: pcI.index },
      ]);
    }
  };

  const cookingDialogProps = useCookingDrawer(
    (date, meal, mealKey, overrides, personId) =>
      onConsume(date, meal, mealKey, personId, overrides),
    onDeleteMeal,
    drawerHistory,
  );
  const planAllDrawerProps = usePlanAllDrawer(
    (date, meal, mealKey) => onConsume(date, meal, mealKey, undefined),
    (c) => cookingDialogProps.cook(getNonEmptyMeals(c)),
    drawerHistory,
  );

  const savePlan = async () => {
    const planCells = getPlanCellsFromTableData(tableData);

    const mealWithIntakes = planCells.filter(
      (planCell) => planCell.indicator == "MealWithIntakes",
    ) as MealWithIntakes[];
    // Neue Mahlzeiten mit bereits erfasstem Verzehr (z. B. Gericht im
    // Verzehr-Drawer gewählt oder ungespeichertes Meal verzehrt) werden als
    // Meal + Intakes in einem Rutsch gespeichert – nicht über
    // `createdMeals` + `createdIntakes`.
    const newMealsWithIntakes = mealWithIntakes.filter(
      (planCell) => isNewCell(planCell) && (planCell.intakes?.length ?? 0) > 0,
    );
    const persistedMealWithIntakes = mealWithIntakes.filter(
      (planCell) => !newMealsWithIntakes.includes(planCell),
    );
    const intakesForMeals = flatMap(
      persistedMealWithIntakes,
      (planCell) => planCell.intakes || [],
    );
    const justIntakes = planCells.filter(
      (planCell) => planCell.indicator == "JustIntake",
    ) as JustIntake[];
    await savePlanRequest({
      body: {
        mealsWithIntakes: newMealsWithIntakes.map((planCell) => ({
          meal: planCell.meal,
          intakes: (planCell.intakes ?? []).filter(
            (intake) => !isDeleted(intake),
          ),
        })),
        createdIntakes: [
          ...intakesForMeals.filter((intakeWithFlags) =>
            isNullIndex(intakeWithFlags.id),
          ),
          ...justIntakes.filter((planCell) => isNewCell(planCell)),
        ],
        deletedIntakes: [
          ...intakesForMeals
            .filter((intakeWithFlags) => isDeleted(intakeWithFlags))
            .map((intakeWithFlags) => intakeWithFlags),
          ...flatMap(
            persistedMealWithIntakes.filter((planCell) => isDeleted(planCell)),
            (p) => p.intakes || [],
          ),
          ...justIntakes.filter((planCell) => isDeleted(planCell)),
        ],
        updatedIntakes: [
          ...intakesForMeals
            .filter((intakeWithFlags) => isEdited(intakeWithFlags))
            .filter((intakeWithFlags) => !isNullIndex(intakeWithFlags.id)),
          ...justIntakes.filter((planCell) => isEdited(planCell)),
        ],
        deletedMeals: persistedMealWithIntakes
          .filter((planCell) => isDeleted(planCell))
          .map((p) => p.meal),
        updatedMeals: persistedMealWithIntakes
          .filter((planCell) => isEdited(planCell))
          .map((p) => p.meal),
        createdMeals: persistedMealWithIntakes
          .filter((planCell) => isNewCell(planCell))
          .map((p) => p.meal),
      },
    });
    updateShouldReload(true);
    updateResetPlan(true);
    await loadTableData();
    updateHasNewData(false);
    return true;
  };

  const [planCellCoordinatesAdd, setPlanCellCoordinatesAdd] =
    useState<PlanCellCoordinates>();

  function handleClickMeal(planCell: PlanCell, planCellIndex: number) {
    setSelectedPlanCells([{ planCell, index: planCellIndex }]);
    onOpenEditPlanCell();
  }

  const [saveIsLoading, setSaveIsLoading] = useState(false);

  const submitPlanCells = (planCells: PlanCellWithIndex[]) => {
    const sortedPlanCells = [...planCells].sort((a, b) => {
      const idxA = typeof a.index === "number" ? a.index : -1;
      const idxB = typeof b.index === "number" ? b.index : -1;
      return idxB - idxA;
    });

    for (const planCell of sortedPlanCells) {
      updateHasNewData(true);
      updateTableData((tableData) => {
        const planCellCoordinates =
          computePlanCellCoordinatesFromPlanCellWithIndex(planCell);
        if (!isNullIndex(planCell.index)) {
          if (isDeleted(planCell.planCell) && isNewCell(planCell.planCell)) {
            getPlanCells(tableData, planCellCoordinates).splice(
              planCell.index,
              1,
            );
            return;
          }
          const planCellFromData = getPlanCell(tableData, planCellCoordinates);
          if (planCellFromData === undefined) return;
          const equal = deepEquals(planCellFromData, planCell.planCell);
          if (!equal) {
            setPlanCell(tableData, planCellCoordinates, {
              ...planCell.planCell,
              edited: true,
            });
          }
        } else {
          if (!isDeleted(planCell.planCell)) {
            getPlanCells(tableData, planCellCoordinates).push(
              planCell.planCell,
            );
          }
        }
      });
    }
  };

  const getTableDataForCoordinates = (
    coords: PlanCellCoordinatesWithOptionalPerson,
  ): PlanCellWithIndex[] => {
    if (coords.index != null && coords.index >= 0 && coords.personId) {
      const planCell = getPlanCell(tableData, coords as PlanCellCoordinates);
      return planCell ? [{ planCell, index: coords.index }] : [];
    }
    return getPlanCellsWithIndex(tableData, coords);
  };

  const mealPlannerContext: MealPlannerContextType = {
    submitPlanCells,
    onConsume,
    getTableDataForCoordinates,
    hasUnsavedChanges: hasNewData,
    requestGroupChange,
  };
  const {
    isOpen: isOpenAddPlanCell,
    onOpen: onOpenAddPlanCell,
    onClose: onCloseAddPlanCell,
  } = usePlan2PlateDisclosure(drawerHistory, "add-plan-cell");
  const {
    isOpen: isOpenEditPlanCell,
    onOpen: onOpenEditPlanCell,
    onClose: onCloseEditPlanCell,
  } = usePlan2PlateDisclosure(drawerHistory, "edit-plan-cell");
  const {
    isOpen: isOpenSettings,
    onOpen: onOpenSettings,
    onClose: onCloseSettings,
  } = usePlan2PlateDisclosure(drawerHistory, "settings");
  const [trackingMode, setTrackingMode] = useTrackingMode();
  const [showHints, setShowHints] = useShowHints();
  const [selectedPlanCells, setSelectedPlanCells] =
    useState<PlanCellWithIndex[]>();
  const [personNeedPersonId, setPersonNeedPersonId] = useState<
    number | undefined
  >();
  const [needDefDraft, updateNeedDefDraft] = useImmer<
    NeedDefinitionWithEditedFlags | undefined
  >(undefined);
  const {
    isOpen: isOpenPersonNeed,
    onOpen: onOpenPersonNeed,
    onClose: onClosePersonNeed,
  } = usePlan2PlateDisclosure(drawerHistory, "person-need");

  // Beim Gruppenwechsel offene, personenbezogene Drawer schließen, damit keine
  // veralteten Koordinaten (Person außerhalb der neuen Gruppe) entstehen.
  // Wichtig: nur tatsächlich offene Drawer schließen – ein onClose auf einen
  // geschlossenen Drawer würde den gemeinsamen History-Stack und damit z. B. den
  // offenen Settings-Drawer schließen.
  const prevDrawerGroupRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      prevDrawerGroupRef.current !== null &&
      prevDrawerGroupRef.current !== activeGroupId
    ) {
      if (isOpenAddPlanCell) onCloseAddPlanCell();
      if (isOpenEditPlanCell) onCloseEditPlanCell();
      if (isOpenPersonNeed) onClosePersonNeed();
      if (planAllDrawerProps.isOpen) planAllDrawerProps.onClose();
      if (cookingDialogProps.isOpen) cookingDialogProps.onClose();
    }
    prevDrawerGroupRef.current = activeGroupId;
  }, [
    activeGroupId,
    isOpenAddPlanCell,
    onCloseAddPlanCell,
    isOpenEditPlanCell,
    onCloseEditPlanCell,
    isOpenPersonNeed,
    onClosePersonNeed,
    planAllDrawerProps.isOpen,
    planAllDrawerProps.onClose,
    cookingDialogProps.isOpen,
    cookingDialogProps.onClose,
  ]);
  const planRef = useRef<HTMLDivElement>(null);
  const loadMoreTop = async () => {
    setIsLoadingUp(true);
    if (isLoadingUp) {
      return;
    }
    const container = planRef.current;
    if (!container) {
      return;
    }
    const prevScrollHeight = container.scrollHeight;
    updateShouldReload(true);
    setStartDate(addDays(startDate, -7));
    requestAnimationFrame(() => {
      const newScrollHeight = container.scrollHeight;
      container.scrollTop += newScrollHeight - prevScrollHeight;
    });
  };
  const loadMoreBot = async () => {
    setIsLoadingDown(true);
    if (isLoadingDown) {
      return;
    }
    updateShouldReload(true);
    setEndDate(addDays(endDate, 7));
  };

  const handleScroll = () => {
    if (singleDayMode) {
      return;
    }
    const container = planRef.current;
    if (!container) return;
    const scrollTop = container.scrollTop;
    if (scrollTop < 60) {
      loadMoreTop();
    }
    const bottomOffset =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (bottomOffset < 50) {
      loadMoreBot();
    }
  };

  // ── Horizontal-Wischen: links = nächster Tag, rechts = vorheriger Tag ─────
  const swipe = useHorizontalSwipe({
    enabled: singleDayMode,
    onSwipeLeft: loadNextDay,
    onSwipeRight: loadPreviousDay,
  });

  // ── Personen-Reihenfolge & mobile 2-Spalten-Sicht ─────────────────────────
  // Aktuell angemeldete Person zuerst, danach die übrigen lexikografisch.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const orderedPersons = useMemo(
    () =>
      [...members].sort((a, b) => {
        if (a.id === personId) {
          return -1;
        }
        if (b.id === personId) {
          return 1;
        }
        return (a.name ?? "").localeCompare(b.name ?? "", "de");
      }),
    [members, personId],
  );

  const otherPersons = useMemo(
    () => orderedPersons.filter((p) => p.id !== personId),
    [orderedPersons, personId],
  );

  // Mobile: nur "ich" + eine per Wisch wählbare weitere Person (sonst zu schmal).
  const [otherIndex, setOtherIndex] = useState(0);
  useEffect(() => {
    setOtherIndex(0);
  }, [activeGroupId]);
  const safeOtherIndex = otherPersons.length
    ? otherIndex % otherPersons.length
    : 0;

  const displayedPersons = useMemo(() => {
    const current =
      orderedPersons.find((p) => p.id === personId) ?? orderedPersons[0];
    if (isMobile && current && otherPersons.length > 0) {
      return [current, otherPersons[safeOtherIndex]].filter(Boolean);
    }
    return orderedPersons;
  }, [isMobile, orderedPersons, otherPersons, safeOtherIndex, personId]);

  const personSwipe = useHorizontalSwipe({
    enabled: isMobile && otherPersons.length > 1,
    stopPropagation: true,
    onSwipeLeft: () => setOtherIndex((i) => (i + 1) % otherPersons.length),
    onSwipeRight: () =>
      setOtherIndex((i) => (i - 1 + otherPersons.length) % otherPersons.length),
  });

  type DayComponent = {
    date: string;
    ref: HTMLDivElement | null;
  };

  const refresh = () => {
    window.location.reload();
  };

  const dayComponents = useRef<DayComponent[]>([]);

  function getNonEmptyMeals({
    date,
    mealInd,
  }: PlanCellCoordinatesWithoutPerson) {
    const meals = getPlanCellsForDateAndMeal(tableData, date, mealInd).filter(
      (planCell) => planCell.indicator == "MealWithIntakes",
    ) as MealWithIntakes[];
    const withoutIntake = meals.filter((meal) => meal.intakes == undefined);
    return withoutIntake.filter((meal) => meal.meal.mealIngredients.length > 0);
  }

  return (
    <LoadingComponent loadData={[loadTableData]} shouldReload={shouldReload}>
      <TooltipProvider>
        <MealPlannerContext.Provider value={mealPlannerContext}>
          <DrawerHistoryContext.Provider value={drawerHistory}>
            <>
              <CookingDrawer {...cookingDialogProps} />
              <PlanAllDrawer {...planAllDrawerProps} />
              <AddPlanCellDrawer
                onClose={onCloseAddPlanCell}
                open={isOpenAddPlanCell}
                planCellCoordinates={planCellCoordinatesAdd}
              />
              <EditPlanCellDrawer
                onClose={onCloseEditPlanCell}
                open={isOpenEditPlanCell}
                planCell={(selectedPlanCells || [])[0]}
              />
              <SettingsDrawer
                open={isOpenSettings}
                onClose={onCloseSettings}
                trackingMode={trackingMode}
                onTrackingModeChange={setTrackingMode}
                showHints={showHints}
                onShowHintsChange={setShowHints}
              />
              <PersonNeedDrawer
                open={isOpenPersonNeed}
                onClose={onClosePersonNeed}
                need={needDefDraft}
                personName={
                  personNeedPersonId
                    ? findById(values(personLookup), personNeedPersonId)?.name
                    : undefined
                }
                onUpdateNeed={(updater) => {
                  updateNeedDefDraft((draft) => {
                    if (draft) updater(draft);
                  });
                }}
                onSave={async () => {
                  if (!needDefDraft) return;
                  await updateNeeds({
                    body: [needDefDraft],
                  });
                  toast({
                    title: "Bedarf gespeichert",
                    status: "success",
                    isClosable: true,
                  });
                  updateShouldReload(true);
                  updateResetPlan(true);
                  await loadTableData();
                  onClosePersonNeed();
                }}
              />
              <ConfirmModal
                open={pendingGroupId !== null}
                title="Gruppe wechseln?"
                description="Es gibt ungespeicherte Änderungen im Plan. Beim Wechsel der Gruppe werden diese verworfen."
                confirmLabel="Wechseln"
                destructive
                dataCy="group-switch-confirm"
                onConfirm={() => {
                  if (pendingGroupId != null) {
                    setActiveGroupId(pendingGroupId);
                  }
                  setPendingGroupId(null);
                }}
                onCancel={() => setPendingGroupId(null)}
              />
              <div
                id={"meal-table"}
                ref={planRef}
                onScroll={handleScroll}
                {...swipe}
                className="h-dvh w-full overflow-y-auto"
              >
                <div className="flex w-full justify-start">
                  <PlanToolbar
                    hasNewData={hasNewData}
                    saveIsLoading={saveIsLoading}
                    singleDayMode={singleDayMode}
                    onSave={() => wrapSave(savePlan, toast, setSaveIsLoading)}
                    onOpenSettings={onOpenSettings}
                    onToggleCalendarMode={() => {
                      if (singleDayMode) {
                        setEndDate(addDays(startDate, 7));
                        updateShouldReload(true);
                      } else {
                        setEndDate(startDate);
                        updateShouldReload(true);
                      }
                      updateSingleDayMode(!singleDayMode);
                    }}
                    onToday={() => {
                      if (singleDayMode) {
                        setStartDate(todayForPlan());
                        setEndDate(todayForPlan());
                        updateShouldReload(true);
                      } else {
                        const today = formatIsoString(todayForPlan());
                        const maybeToday = dayComponents.current.find(
                          (d) => d.date == today,
                        );
                        if (maybeToday) {
                          maybeToday.ref?.scrollIntoView();
                        } else {
                          setStartDate(todayForPlan());
                          setEndDate(addDays(todayForPlan(), 7));
                          updateShouldReload(true);
                        }
                      }
                    }}
                    onRefresh={refresh}
                  />
                  <div className="fixed left-0 right-0 flex top-[60px] z-[1] mx-auto max-w-[800px]">
                    <div
                      className=" flex h-10 w-full  items-center gap-2 bg-[var(--p2p-bg)] px-4 ml-[15px]"
                      {...personSwipe}
                    >
                      {displayedPersons.map((person, index) => (
                        <React.Fragment key={person.id}>
                          {index > 0 && (
                            <div
                              key={"divider-" + person.id}
                              className="h-auto w-px self-stretch bg-line"
                            />
                          )}
                          <div
                            key={"link-" + person.id}
                            data-cy={`person-link-${person.id}`}
                            className="flex h-full flex-1 items-center justify-center cursor-pointer text-center text-base text-ink"
                            onClick={() => {
                              const needTimeslice = getNeedForDate(
                                person.needs,
                                formatIsoString(todayForPlan()),
                              );
                              if (needTimeslice) {
                                updateNeedDefDraft({
                                  ...needToNeedDefinition(needTimeslice),
                                  edited: false,
                                  deleted: false,
                                });
                                setPersonNeedPersonId(person.id);
                                onOpenPersonNeed();
                              }
                            }}
                          >
                            {person.name}
                          </div>
                        </React.Fragment>
                      ))}
                      {isMobile && otherPersons.length > 1 && (
                        <div
                          className="flex shrink-0 items-center gap-1 pl-1"
                          data-cy="person-swipe-dots"
                        >
                          {otherPersons.map((other, i) => (
                            <span
                              key={other.id}
                              className={`h-1.5 w-1.5 rounded-full ${
                                i === safeOtherIndex ? "bg-teal" : "bg-line"
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex w-full max-w-[800px] flex-col gap-2 px-4 pt-0">
                    <div className="flex flex-col gap-2">
                      {keys(tableData)
                        .sort()
                        .map((date, dateIndex) => {
                          const dateParsed = parseDate(date);
                          const mealsForDate = tableData[date];
                          return (
                            <div
                              ref={(ref) => {
                                dayComponents.current[dateIndex] = {
                                  date,
                                  ref,
                                };
                              }}
                              className="ml-[15px] flex w-[calc(100%-15px)] flex-col items-stretch gap-2"
                              key={date}
                            >
                              <div className="sticky top-[10px] z-[1] flex w-fit items-center gap-2 bg-[var(--p2p-bg)] pb-3.5">
                                <div className="w-[30px]">
                                  <p className="text-xl text-ink">
                                    {dateParsed.getDate()}
                                  </p>
                                  <p className="text-xs text-ink">
                                    {new Intl.DateTimeFormat("de-DE", {
                                      weekday: "short",
                                    }).format(dateParsed)}
                                  </p>
                                </div>
                              </div>
                              <div className="relative pt-[15px]" key={date}>
                                <div className="flex items-start gap-2">
                                  <div className="flex w-full flex-col gap-2">
                                    <div className="flex w-full items-stretch gap-2"></div>
                                    {keys(mealsForDate)
                                      .sort(compareMealInds)
                                      .map((mealInd) => {
                                        const mealsForInd =
                                          mealsForDate[mealInd];
                                        return (
                                          <div
                                            className="relative mt-2.5 flex rounded-card border border-line p-4 pb-[5px] shadow-card"
                                            key={mealInd}
                                          >
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Plan2PlateIconButton
                                                  data-cy="cooking-dialog-button"
                                                  position="absolute"
                                                  top={"-16px"}
                                                  left={"-20px"}
                                                  aria-label="cookingdialog"
                                                  title="Kochdialog"
                                                  variant={"ghost"}
                                                  icon={mealInds[mealInd].icon}
                                                  onClick={() => {
                                                    const coordinates = {
                                                      date,
                                                      mealInd,
                                                    };
                                                    const nonEmptyMeals =
                                                      getNonEmptyMeals(
                                                        coordinates,
                                                      );
                                                    const unsavedMeals =
                                                      nonEmptyMeals.filter(
                                                        (m) => isNewCell(m),
                                                      );
                                                    const hasNonEmptyMeals =
                                                      nonEmptyMeals.length > 0;
                                                    const hasUnsavedMeals =
                                                      unsavedMeals.length > 0;
                                                    planAllDrawerProps.open(
                                                      coordinates,
                                                      hasNonEmptyMeals,
                                                      hasUnsavedMeals,
                                                    );
                                                  }}
                                                />
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                {`${mealInds[mealInd].name}-Kochdialog`}
                                              </TooltipContent>
                                            </Tooltip>
                                            <div className="mt-2 flex flex-1 gap-2">
                                              {displayedPersons.map(
                                                (person, index) => {
                                                  const personId = person.id;
                                                  return (
                                                    <React.Fragment
                                                      key={personId}
                                                    >
                                                      {index > 0 && (
                                                        <div
                                                          key={
                                                            "divider-" +
                                                            personId
                                                          }
                                                          className="w-px self-stretch bg-line"
                                                        />
                                                      )}
                                                      <div
                                                        key={
                                                          "stack-" + personId
                                                        }
                                                        className="flex h-full flex-1 flex-col items-stretch justify-between gap-2"
                                                      >
                                                        <div className="flex flex-1 flex-wrap">
                                                          {mealsForInd[
                                                            personId
                                                          ].map(
                                                            (
                                                              planCell,
                                                              mealIndex,
                                                            ) => {
                                                              return (
                                                                <PlanCellTag
                                                                  key={
                                                                    mealIndex
                                                                  }
                                                                  planCell={
                                                                    planCell
                                                                  }
                                                                  mealIndex={
                                                                    mealIndex
                                                                  }
                                                                  onClick={() =>
                                                                    handleClickMeal(
                                                                      planCell,
                                                                      mealIndex,
                                                                    )
                                                                  }
                                                                  purchases={
                                                                    purchases
                                                                  }
                                                                />
                                                              );
                                                            },
                                                          )}
                                                        </div>
                                                        <div className="flex flex-1 items-end justify-center gap-5">
                                                          <Plan2PlateIconButton
                                                            aria-label="more"
                                                            data-cy="more-button"
                                                            title="Mehr anzeigen"
                                                            icon="dotsHorizontal"
                                                            onClick={() => {
                                                              setPlanCellCoordinatesAdd(
                                                                {
                                                                  date,
                                                                  mealInd,
                                                                  personId,
                                                                },
                                                              );
                                                              onOpenAddPlanCell();
                                                            }}
                                                          />
                                                        </div>
                                                      </div>
                                                    </React.Fragment>
                                                  );
                                                },
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    <div className="flex w-full items-stretch gap-2">
                                      <div className="flex w-full gap-2">
                                        {displayedPersons.map(
                                          (person, index) => {
                                            const personId = person.id;
                                            const personNeeds = person.needs;
                                            const currentNeed = getNeedForDate(
                                              personNeeds,
                                              date,
                                            );
                                            const planCellsForDay = flatMap(
                                              flatMap(tableData[date], (x) =>
                                                values(x),
                                              ),
                                              (x) => values(x),
                                            )
                                              .filter(
                                                (planCell) =>
                                                  computePlanCellCoordinates(
                                                    planCell,
                                                  ).personId == personId,
                                              )
                                              .filter(
                                                (planCell) =>
                                                  !isDeleted(planCell),
                                              );

                                            const nutriValues = flatMap(
                                              planCellsForDay,
                                              (pc) =>
                                                mapPlanCellToNutriValues(
                                                  pc,
                                                  foods,
                                                  purchases,
                                                ),
                                            );

                                            return (
                                              <React.Fragment
                                                key={personId}
                                              >
                                                {index > 0 && (
                                                  <div
                                                    key={
                                                      "divider-" +
                                                      personId
                                                    }
                                                    className="w-px self-stretch bg-line"
                                                  />
                                                )}
                                                <AllNutriValues
                                                  nutriValues={nutriValues}
                                                  needForDate={currentNeed}
                                                  mode={"vertical"}
                                                  trackingMode={trackingMode}
                                                />
                                              </React.Fragment>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="w-full border-t border-line" />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          </DrawerHistoryContext.Provider>
        </MealPlannerContext.Provider>
      </TooltipProvider>
    </LoadingComponent>
  );
};

export default MealTable;
